import React, { useState, useEffect } from 'react';
import { Modal, Tabs, Empty, Input } from 'antd';
import { CheckOutlined, SearchOutlined, RightOutlined, DownOutlined, LoadingOutlined } from '@ant-design/icons';
import axios from 'axios';
import ENVIRONMENT from '../../../environments';

const { TabPane } = Tabs;

function groupByCompany(data) {
    const map = data.reduce((acc, customer) => {
        if (!acc[customer.company_id]) {
            acc[customer.company_id] = { company_name: customer.company_name, customers: [] };
        }
        acc[customer.company_id].customers.push(customer);
        return acc;
    }, {});

    Object.values(map).forEach(c =>
        c.customers.sort((a, b) => a.customer_username.localeCompare(b.customer_username))
    );

    return Object.entries(map).sort(([, a], [, b]) => a.company_name.localeCompare(b.company_name));
}

function displayName(customer) {
    return customer.customer_username?.startsWith('Google')
        ? customer.customer_email?.split('@')[0]
        : customer.customer_username;
}

function CustomerList({ grouped, activeUsername, onSelect, searchQuery }) {
    const [expandedCompanies, setExpandedCompanies] = useState({});
    const isSearching = searchQuery.trim().length > 0;

    const filtered = grouped
        .map(([companyId, companyData]) => {
            const customers = companyData.customers.filter(c => {
                const name = displayName(c)?.toLowerCase() || '';
                const email = (c.customer_email || '').toLowerCase();
                const q = searchQuery.toLowerCase();
                return name.includes(q) || email.includes(q);
            });
            return [companyId, { ...companyData, customers }];
        })
        .filter(([, companyData]) => companyData.customers.length > 0);

    if (filtered.length === 0) {
        return (
            <Empty
                style={{ marginTop: 20, marginBottom: 20 }}
                description="No customers found."
                className="manrope f-14"
            />
        );
    }

    const toggleCompany = (companyId) => {
        setExpandedCompanies(prev => ({ ...prev, [companyId]: !prev[companyId] }));
    };

    return (
        <div>
            {filtered.map(([companyId, companyData]) => {
                const isExpanded = isSearching || !!expandedCompanies[companyId];
                return (
                    <div key={companyId}>
                        <div
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                            onClick={() => toggleCompany(companyId)}
                        >
                            <span className="manrope f-14 w-600">{companyData.company_name}</span>
                            {isExpanded
                                ? <DownOutlined style={{ fontSize: 11, color: '#888' }} />
                                : <RightOutlined style={{ fontSize: 11, color: '#888' }} />}
                        </div>
                        {isExpanded && companyData.customers.map((customer, ci) => (
                            <div
                                key={ci}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 12px 11px 24px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                onClick={() => onSelect(customer)}
                            >
                                <span className="manrope f-14 w-400">{displayName(customer)}</span>
                                {activeUsername === customer.customer_username && (
                                    <CheckOutlined style={{ color: '#276DD7', fontSize: 12 }} />
                                )}
                            </div>
                        ))}
                    </div>
                );
            })}
        </div>
    );
}

function TabContent({ grouped, activeUsername, onSelect, onResetDefault, searchQuery, setSearchQuery, loading }) {
    return (
        <>
            <Input
                prefix={<SearchOutlined />}
                placeholder="Search by name or email"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ margin: '12px 0', height: 40 }}
                />
            {onResetDefault && (
                <div
                    style={{ padding: '12px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                    onClick={onResetDefault}
                >
                    <span className="manrope f-15 w-700 black-65">Default User</span>
                </div>
            )}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <LoadingOutlined style={{ fontSize: 24, color: '#276DD7' }} />
                </div>
            ) : grouped.length === 0 ? (
                <Empty
                    style={{ marginTop: 20, marginBottom: 20 }}
                    description="No customers found."
                    className="manrope f-14"
                />
            ) : (
                <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                    <CustomerList
                        grouped={grouped}
                        activeUsername={activeUsername}
                        onSelect={onSelect}
                        searchQuery={searchQuery}
                    />
                </div>
            )}
        </>
    );
}

export default function CustomerSwitchModal({ visible, onClose, assignedCustomers, isSuperAdmin, onAssign, onAssumeRole, onResetDefault, activeUsername }) {
    const [recentUsers, setRecentUsers] = useState([]);
    const [recentLoading, setRecentLoading] = useState(false);
    const [switching, setSwitching] = useState(false);
    const [searchAssigned, setSearchAssigned] = useState('');
    const [searchRecent, setSearchRecent] = useState('');

    useEffect(() => {
        if (!visible) {
            setSwitching(false);
            return;
        }
        if (!isSuperAdmin) return;

        setRecentLoading(true);
        let username = localStorage.getItem('username');
        if (localStorage.getItem('super_admin_username')) {
            username = localStorage.getItem('super_admin_username');
        }
        axios.post(ENVIRONMENT.GET_RECENTLY_ACTIVE_USERS, { designer_username: username })
            .then(res => { if (res.data) setRecentUsers(res.data); })
            .catch(() => {})
            .finally(() => setRecentLoading(false));
    }, [visible, isSuperAdmin]);

    const withLoader = (fn) => (...args) => {
        setSwitching(true);
        fn(...args);
    };

    const assignedGrouped = groupByCompany(
        assignedCustomers.flatMap(([, companyData]) => companyData.customers)
    );
    const recentGrouped = groupByCompany(recentUsers);

    return (
        <Modal
            visible={visible}
            onCancel={switching ? null : onClose}
            closable={!switching}
            maskClosable={!switching}
            className="modal-share"
            footer={null}
        >
            <div style={{ position: 'relative' }}>
                {switching && (
                    <div style={{ position: 'absolute', inset: 0, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', borderRadius: 8 }}>
                        <LoadingOutlined style={{ fontSize: 28, color: '#276DD7' }} />
                        <span className="manrope f-14 w-500" style={{ marginTop: 12, color: '#276DD7' }}>Switching user...</span>
                    </div>
                )}
                <h2 className="manrope f-18 black-00 w-600">Switch Customer</h2>
                <Tabs>
                    <TabPane tab="Assigned Users" key="1">
                        <TabContent
                            grouped={assignedGrouped}
                            activeUsername={activeUsername}
                            onSelect={withLoader(isSuperAdmin ? onAssumeRole : onAssign)}
                            onResetDefault={onResetDefault ? withLoader(onResetDefault) : null}
                            searchQuery={searchAssigned}
                            setSearchQuery={setSearchAssigned}
                            loading={false}
                        />
                    </TabPane>

                    {isSuperAdmin && (
                        <TabPane tab="Recently Active Users" key="2">
                            <TabContent
                                grouped={recentGrouped}
                                activeUsername={activeUsername}
                                onSelect={withLoader(onAssumeRole)}
                                onResetDefault={onResetDefault ? withLoader(onResetDefault) : null}
                                searchQuery={searchRecent}
                                setSearchQuery={setSearchRecent}
                                loading={recentLoading}
                            />
                        </TabPane>
                    )}
                </Tabs>
            </div>
        </Modal>
    );
}
